import { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { IconPlus, IconCreditCard } from '@tabler/icons-react';
import ApiHandler from '@api/ApiHandler';
import { urls } from '@api/urls';
import { PaymentMethod } from '@app/types/payment';
import { BrandType } from '@static/staticData';

interface PaymentMethodSelectorProps {
  onSelect: (paymentMethodId: string) => void;
  onAddNew: () => void;
  selectedPaymentMethodId?: string;
  /** If true, automatically call onAddNew when no payment methods exist */
  autoAddWhenEmpty?: boolean;
}

function PaymentMethodSelector({
  onSelect,
  onAddNew,
  selectedPaymentMethodId,
  autoAddWhenEmpty = true,
}: PaymentMethodSelectorProps) {
  const theme = useTheme();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string>(
    selectedPaymentMethodId || ''
  );
  const [autoAddTriggered, setAutoAddTriggered] = useState(false);

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  // Auto-trigger onAddNew when no payment methods exist and autoAddWhenEmpty is true
  useEffect(() => {
    if (
      !loading &&
      !error &&
      paymentMethods.length === 0 &&
      autoAddWhenEmpty &&
      !autoAddTriggered
    ) {
      setAutoAddTriggered(true);
      onAddNew();
    }
  }, [
    loading,
    error,
    paymentMethods.length,
    autoAddWhenEmpty,
    autoAddTriggered,
    onAddNew,
  ]);

  useEffect(() => {
    // Auto-select default card if none selected
    if (!selectedId && paymentMethods.length > 0) {
      const defaultCard = paymentMethods.find((pm) => pm.is_active);
      if (defaultCard) {
        setSelectedId(defaultCard.id);
        onSelect(defaultCard.id);
      } else {
        setSelectedId(paymentMethods[0].id);
        onSelect(paymentMethods[0].id);
      }
    }
  }, [paymentMethods, selectedId, onSelect]);

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: any = await ApiHandler.get(
        urls.caseSubscription.paymentMethods
      );
      setPaymentMethods(response.formatted_payments_methods || []);
    } catch (e: any) {
      console.error('Error fetching payment methods:', e);
      setError('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const id = event.target.value;
    setSelectedId(id);
    onSelect(id);
  };

  const getBrandIcon = (brand: string) => {
    const brandLower = brand.toLowerCase();
    switch (brandLower) {
      case BrandType.Visa:
        return <img src="/visa.svg" alt="Visa" style={{ height: 24 }} />;
      case BrandType.MasterCard:
        return (
          <img src="/mastercard.svg" alt="Mastercard" style={{ height: 24 }} />
        );
      case BrandType.AmericanExpress:
        return <img src="/amex.svg" alt="Amex" style={{ height: 24 }} />;
      case BrandType.Discover:
        return (
          <img src="/discover.svg" alt="Discover" style={{ height: 24 }} />
        );
      case BrandType.DinersClub:
        return <img src="/diners.svg" alt="Diners" style={{ height: 24 }} />;
      default:
        return <IconCreditCard size={24} />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box py={2}>
        <Typography color="error" variant="body2">
          {error}
        </Typography>
        <Button size="small" onClick={fetchPaymentMethods} sx={{ mt: 1 }}>
          Retry
        </Button>
      </Box>
    );
  }

  if (paymentMethods.length === 0) {
    // If autoAddWhenEmpty is true, the useEffect will handle redirecting
    // Show a loading state while redirecting, or manual button if auto is disabled
    if (autoAddWhenEmpty) {
      return (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    return (
      <Box py={2}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          No saved payment methods found.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<IconPlus size={18} />}
          onClick={onAddNew}
        >
          Add Payment Method
        </Button>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2" fontWeight={600}>
        Select Payment Method
      </Typography>

      <RadioGroup value={selectedId} onChange={handleChange}>
        {paymentMethods.map((pm) => (
          <FormControlLabel
            key={pm.id}
            value={pm.id}
            control={<Radio size="small" />}
            sx={{
              m: 0,
              p: 1.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor:
                selectedId === pm.id
                  ? theme.palette.primary.main
                  : theme.palette.divider,
              backgroundColor:
                selectedId === pm.id
                  ? theme.palette.primary.light
                  : 'transparent',
              mb: 1,
              '&:last-child': { mb: 0 },
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: theme.palette.primary.main,
              },
            }}
            label={
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ ml: 1 }}
              >
                <Box sx={{ minWidth: 40 }}>{getBrandIcon(pm.brand)}</Box>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    •••• •••• •••• {pm.last4}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Expires {pm.exp_month}/{pm.exp_year}
                    {pm.is_active && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          ml: 1,
                          color: theme.palette.success.main,
                          fontWeight: 600,
                        }}
                      >
                        Default
                      </Typography>
                    )}
                  </Typography>
                </Box>
              </Stack>
            }
          />
        ))}
      </RadioGroup>

      <Button
        variant="text"
        size="small"
        startIcon={<IconPlus size={16} />}
        onClick={onAddNew}
        sx={{ alignSelf: 'flex-start' }}
      >
        Use a different card
      </Button>
    </Stack>
  );
}

export default PaymentMethodSelector;
